import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Stethoscope, Terminal, Brain, CheckCircle, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';

const Services = () => {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    let sectionId = null;
    if (path.includes('medical-devices')) sectionId = 'medical-devices';
    else if (path.includes('software')) sectionId = 'software';
    else if (path.includes('ai')) sectionId = 'ai';
    if (sectionId) {
      setTimeout(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [location]);

  const overviewCards = [
    {
      icon: Stethoscope,
      title: 'Medical Device Development',
      description: 'End-to-end medical device consulting from concept through to post-market surveillance, ensuring regulatory compliance at every stage.',
      anchor: '#medical-devices',
    },
    {
      icon: Terminal,
      title: 'Software Engineering',
      description: 'Scalable, secure software solutions built with modern architectures and best practices for healthcare and beyond.',
      anchor: '#software',
    },
    {
      icon: Brain,
      title: 'AI Integration & Consultancy',
      description: 'Strategic AI adoption and custom intelligent solutions that transform data into actionable business outcomes.',
      anchor: '#ai',
    },
  ];

  const medicalFeatures = [
    'Concept Development & Feasibility',
    'Design Controls & V&V',
    'Regulatory Strategy (FDA, TGA, CE)',
    'Quality Management Systems',
    'Clinical Evaluation',
    'Post-Market Surveillance',
  ];

  const softwareFeatures = [
    'Full-Stack Web Applications',
    'Cloud Architecture & DevOps',
    'Mobile App Development',
    'API Design & Integration',
    'SaMD (Software as Medical Device)',
    'Legacy System Modernization',
  ];

  const aiFeatures = [
    'Machine Learning & Deep Learning',
    'NLP',
    'Computer Vision',
    'Predictive Analytics',
    'AI Strategy & Roadmapping',
    'MLOps & Model Deployment',
  ];

  const processSteps = [
    { number: '01', title: 'Discovery', description: 'We dive deep into your challenges, goals, and constraints to fully understand the problem space.' },
    { number: '02', title: 'Strategy', description: 'We craft a tailored roadmap with clear milestones, technology choices, and risk mitigation plans.' },
    { number: '03', title: 'Execution', description: 'Our team builds iteratively with continuous feedback loops, ensuring quality and alignment at every sprint.' },
    { number: '04', title: 'Delivery', description: 'We deploy, validate, and hand over with full documentation, training, and ongoing support options.' },
  ];

  return (
    <>
      <SEO
        title="Services"
        description="Medical device development, software engineering, and AI consulting services. From concept through regulatory approval to market — FDA, CE Mark, and TGA expertise."
        path="/services"
      />
    <div className="bg-[#0a0f1a] text-white min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Glow orbs */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-cyan-500/20 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-indigo-500/20 rounded-full blur-[120px]" />

        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-cyan-400 mb-4">
            OUR SERVICES
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight">
            End-to-End <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-indigo-400">
              Technology
            </span>{' '}
            Solutions
          </h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto mt-6">
            We partner with ambitious teams to design, build, and scale technology
            products across medical devices, software, and artificial intelligence.
          </p>
        </div>
      </section>

      {/* Overview Cards */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {overviewCards.map((card) => {
              const Icon = card.icon;
              return (
                <a
                  key={card.title}
                  href={card.anchor}
                  className="group bg-white/[0.04] backdrop-blur-xl border border-white/[0.06] rounded-2xl p-8 transition-all duration-300 hover:bg-white/[0.08] hover:border-white/[0.12] hover:-translate-y-1"
                >
                  <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center mb-5">
                    <Icon className="w-6 h-6 text-cyan-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">{card.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed mb-6">{card.description}</p>
                  <span className="inline-flex items-center text-cyan-400 text-sm font-medium group-hover:gap-2 transition-all duration-300">
                    Learn More <ArrowRight className="w-4 h-4 ml-1" />
                  </span>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Medical Devices Section */}
      <section id="medical-devices" className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left: Content */}
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-cyan-400 mb-4">
                MEDICAL DEVICES
              </p>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white tracking-tight mb-6">
                Pioneering Medical Device Innovation
              </h2>
              <p className="text-slate-400 text-lg leading-relaxed mb-8">
                From initial concept through regulatory approval and beyond, we guide
                medical device companies through every stage of the product lifecycle
                with deep domain expertise and hands-on execution.
              </p>
              <ul className="space-y-4">
                {medicalFeatures.map((feature) => (
                  <li key={feature} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <span className="text-slate-300">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: Decorative glass card */}
            <div className="relative">
              <div className="bg-white/[0.04] backdrop-blur-xl border border-white/[0.06] rounded-2xl p-10 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-bl from-cyan-500/20 to-transparent rounded-full blur-2xl" />
                <div className="absolute bottom-0 left-0 w-40 h-40 bg-gradient-to-tr from-indigo-500/20 to-transparent rounded-full blur-2xl" />
                <div className="relative flex items-center justify-center h-64">
                  <Stethoscope className="w-24 h-24 text-cyan-400/30" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Software Section */}
      <section id="software" className="py-24 bg-[#111827]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left: Decorative glass card */}
            <div className="relative order-2 lg:order-1">
              <div className="bg-white/[0.04] backdrop-blur-xl border border-white/[0.06] rounded-2xl p-10 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-40 h-40 bg-gradient-to-br from-cyan-500/20 to-transparent rounded-full blur-2xl" />
                <div className="absolute bottom-0 right-0 w-40 h-40 bg-gradient-to-tl from-indigo-500/20 to-transparent rounded-full blur-2xl" />
                <div className="relative flex items-center justify-center h-64">
                  <Terminal className="w-24 h-24 text-cyan-400/30" />
                </div>
              </div>
            </div>

            {/* Right: Content */}
            <div className="order-1 lg:order-2">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-cyan-400 mb-4">
                SOFTWARE
              </p>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white tracking-tight mb-6">
                Scalable Software Solutions
              </h2>
              <p className="text-slate-400 text-lg leading-relaxed mb-8">
                We architect and deliver production-grade software systems that are
                secure, performant, and built to evolve with your business needs.
              </p>
              <ul className="space-y-4">
                {softwareFeatures.map((feature) => (
                  <li key={feature} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <span className="text-slate-300">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* AI Section */}
      <section id="ai" className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left: Content */}
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-cyan-400 mb-4">
                ARTIFICIAL INTELLIGENCE
              </p>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white tracking-tight mb-6">
                Intelligent AI Solutions
              </h2>
              <p className="text-slate-400 text-lg leading-relaxed mb-8">
                Harness the power of artificial intelligence with custom models,
                strategic consulting, and production-ready deployments that deliver
                measurable impact.
              </p>
              <ul className="space-y-4">
                {aiFeatures.map((feature) => (
                  <li key={feature} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <span className="text-slate-300">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: Decorative glass card */}
            <div className="relative">
              <div className="bg-white/[0.04] backdrop-blur-xl border border-white/[0.06] rounded-2xl p-10 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-bl from-cyan-500/20 to-transparent rounded-full blur-2xl" />
                <div className="absolute bottom-0 left-0 w-40 h-40 bg-gradient-to-tr from-indigo-500/20 to-transparent rounded-full blur-2xl" />
                <div className="relative flex items-center justify-center h-64">
                  <Brain className="w-24 h-24 text-cyan-400/30" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-[#111827]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-cyan-400 mb-4">
              HOW WE WORK
            </p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white tracking-tight">
              Our Proven Process
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {processSteps.map((step) => (
              <div
                key={step.number}
                className="bg-white/[0.04] backdrop-blur-xl border border-white/[0.06] rounded-2xl p-8"
              >
                <span className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-indigo-400 block mb-4">
                  {step.number}
                </span>
                <h3 className="text-xl font-semibold text-white mb-3">{step.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-6">
          <div className="rounded-3xl bg-white/[0.04] backdrop-blur-xl border border-white/[0.06] overflow-hidden relative">
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 to-indigo-500/10" />
            <div className="relative px-8 py-16 sm:px-16 text-center">
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white tracking-tight mb-6">
                Ready to Start Your Project?
              </h2>
              <p className="text-slate-400 text-lg max-w-2xl mx-auto mb-8">
                Let us turn your vision into reality. Reach out to discuss how we can
                help you build, scale, and innovate.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-cyan-500 to-indigo-500 text-white font-semibold rounded-xl hover:opacity-90 transition-opacity duration-300"
              >
                Get in Touch <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Services;
